import { Device } from '@twilio/voice-sdk'
import CallDeviceManager from 'messaging/calling/CallDeviceManager'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'

export default class TwilioVoiceCallSettingsModalViewModel {
  @observable showing = false

  @observable.ref device: Device | null = null
  @observable canSetOutput: boolean = false
  @observable canAnalyzeOutput: boolean = false

  /**
   * The selected device ID.
   * Hydrated during activation and also set via UI
   *
   * `default` is a safe callback according to the specs
   * https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices#return_value
   */
  @observable selectedInputDeviceId: string = 'default'
  @observable selectedOutputDeviceId: string = 'default'

  constructor(private deviceManager: CallDeviceManager) {
    makeObservable(this)
  }

  /**
   * Sets the twilio device. Called during activation
   * @param device
   */
  @action
  setDevice(device: Device | null) {
    this.device = device
  }

  /**
   * Audio Input
   */

  @computed
  get inputDevices() {
    if (!this.device || !this.device.audio) {
      return null
    }

    return this.device.audio.availableInputDevices
  }

  @computed
  get inputDeviceList() {
    if (!this.inputDevices) {
      return []
    }

    return Array.from(this.inputDevices.values())
  }

  @action
  setSelectedInputDeviceId(id: string) {
    this.selectedInputDeviceId = id
  }

  setAudioInputDevice = task.resolved(async (device?: MediaDeviceInfo) => {
    if (!this.device || !device) {
      return
    }

    // Select Device
    await this.device.audio?.setInputDevice(device.deviceId)
    this.setSelectedInputDeviceId(device.deviceId)
    this.deviceManager.persistPreviousDevice(device)
  })

  @computed
  get selectedAudioInputDevice() {
    return this.inputDevices?.get(this.selectedInputDeviceId) ?? null
  }

  /**
   * Audio Output
   */

  @computed
  get outputDevices() {
    if (!this.device || !this.device.audio) {
      return null
    }

    return this.device.audio.availableOutputDevices
  }

  @computed
  get outputDeviceList() {
    if (!this.outputDevices) {
      return []
    }

    return Array.from(this.outputDevices.values())
  }

  @action
  setSelectedOutputDeviceId(id: string) {
    this.selectedOutputDeviceId = id
  }

  @computed
  get selectedAudioOutputDevice() {
    return this.outputDevices?.get(this.selectedOutputDeviceId) ?? null
  }

  setAudioOutputDevice = task.resolved(async (device?: MediaDeviceInfo) => {
    if (!this.device || !device) {
      return
    }

    // Select Device
    await this.device.audio?.speakerDevices.set([device.deviceId])
    this.setSelectedOutputDeviceId(device.deviceId)
    this.deviceManager.persistPreviousDevice(device)
  })

  @computed
  get canTestOutput() {
    return this.canSetOutput && !this.device?.isBusy
  }

  testOutput = task.resolved(async () => {
    if (!this.device || !this.device.audio) {
      return
    }

    await this.device.audio.speakerDevices.test()
    // Let the test audio sound play fully before going back to resolved
    return await new Promise((resolve) => setTimeout(resolve, 1500))
  })

  @action
  setCanSetOutput(can: boolean) {
    this.canSetOutput = can
  }

  @action
  setCanAnalyzeOutput(can: boolean) {
    this.canAnalyzeOutput = can
  }

  /**
   * Activate sets up all the necessary state
   * from the given device.
   *
   * This module is meant to be activated with a new device
   * when a new one is available. Usually when showing the modal
   * @param device
   * @returns
   */
  async activate(device: Device) {
    const micPermissions = await navigator.permissions.query({
      // Current built in types don't have this yet
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      name: 'microphone',
    })

    // If microphone permissions haven't been granted
    // we get the user media to trigger the permission prompt
    // and release the returned media streams right away.
    // Twilio will handle the rest.
    if (micPermissions.state !== 'granted') {
      await navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: false,
        })
        .then((s) =>
          s.getTracks().forEach((t) => {
            t.stop()
            s.removeTrack(t)
          })
        )
    }

    this.setDevice(device)

    if (!this.device || !this.device.audio) {
      // Not much to do without a device that can make calls
      return
    }

    // Hydrate device capabilities
    const canSetOuptut = this.device.audio.isOutputSelectionSupported
    if (canSetOuptut) {
      this.setCanSetOutput(canSetOuptut)
    }

    if (this.device.audio.isVolumeSupported) {
      this.setCanAnalyzeOutput(true)
    }

    // Hydrate Previous Input Device
    const availableInput = this.device.audio.availableInputDevices
    const previousInId = this.deviceManager.getPreviousDevice('audioinput')
    if (previousInId) {
      const previousIn = availableInput.get(previousInId)
      if (previousIn) {
        this.device.audio.setInputDevice(previousIn.deviceId)
        this.setSelectedInputDeviceId(previousInId)
      }
    }

    // Hydrate Previous Output Device
    const availableOutput = this.device.audio.availableOutputDevices
    const previousOutId = this.deviceManager.getPreviousDevice('audiooutput')
    if (previousOutId && canSetOuptut) {
      const previousIn = availableOutput.get(previousOutId)
      if (previousIn) {
        this.device.audio.speakerDevices.set(previousIn.deviceId)
        this.setSelectedOutputDeviceId(previousOutId)
      }
    }
  }

  @action.bound
  show() {
    this.showing = true
  }

  @action.bound
  hide() {
    this.showing = false
  }
}
